import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import {
  ACCEPT_LABEL,
  CHECKOUT_ACK_LABEL,
  DISCLAIMER_HEADING,
  FULL_DISCLAIMER,
  ROLE_RESPONSIBILITIES,
  SHORT_DISCLAIMER,
  bi,
  biList,
} from '@/data/legal';
import { UserType } from '@/data/constants';

/**
 * Marketplace liability notice. `variant="short"` is the compact one-liner used
 * inside checkout, `variant="full"` lists every limitation and (optionally) the
 * role specific verification duties.
 */
export const MarketplaceDisclaimer: React.FC<{
  variant?: 'short' | 'full';
  role?: UserType | null;
  className?: string;
}> = ({ variant = 'short', role = null, className = '' }) => {
  const { lang } = useI18n();
  const resp = role ? ROLE_RESPONSIBILITIES[role as keyof typeof ROLE_RESPONSIBILITIES] : null;

  return (
    <div className={`rounded-xl border border-amber-200 bg-amber-50 p-4 ${className}`}>
      <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-800">
        <ShieldAlert className="h-4 w-4" /> {bi(DISCLAIMER_HEADING, lang)}
      </p>
      {variant === 'short' ? (
        <p className="mt-2 text-xs leading-relaxed text-amber-900">{bi(SHORT_DISCLAIMER, lang)}</p>
      ) : (
        <div className="mt-2 space-y-2">
          {biList(FULL_DISCLAIMER, lang).map((p) => (
            <p key={p.slice(0, 24)} className="text-xs leading-relaxed text-amber-900">
              {p}
            </p>
          ))}
          {resp && (
            <div className="pt-1">
              <p className="text-xs font-bold text-amber-900">{bi(resp.title, lang)}</p>
              <ul className="mt-1 list-disc pl-5 text-xs text-amber-900">
                {biList(resp.items, lang).map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Required acceptance checkbox. Used on every registration form and on both
 * checkout flows (lead purchase + subscription).
 */
export const AcceptTermsCheckbox: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  kind?: 'register' | 'checkout';
  className?: string;
}> = ({ checked, onChange, kind = 'register', className = '' }) => {
  const { lang, path } = useI18n();
  const label = kind === 'checkout' ? CHECKOUT_ACK_LABEL : ACCEPT_LABEL;

  return (
    <label className={`flex cursor-pointer items-start gap-2.5 text-xs leading-relaxed text-slate-600 ${className}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-slate-400 accent-orange-500"
      />
      <span>
        {bi(label, lang)}{' '}
        {kind === 'register' && (
          <>
            <Link to={path('/terms')} target="_blank" className="font-bold text-orange-600 underline">
              {lang === 'es' ? 'Términos de Servicio' : 'Terms of Service'}
            </Link>
            {' · '}
            <Link to={path('/privacy')} target="_blank" className="font-bold text-orange-600 underline">
              {lang === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
            </Link>
          </>
        )}
      </span>
    </label>
  );
};

export default MarketplaceDisclaimer;
